<template>
  <section class="header-container" :style="generateCssVariables">
    <picture class="figure0 d-none d-xl-flex">
      <source
        v-for="{ resolution, size } in imageSizes"
        :key="resolution"
        :media="`(max-width: ${resolution}px)`"
        :srcset="`${imageLeft.url}&w=${size}&fmt=webp`"
      />
      <img :src="imageLeft.url" :alt="imageLeft.alt" height="100%" width="auto" />
    </picture>
    <div class="content-container d-flex flex-column justify-content-center align-items-center">
      <div class="title-container text-center">
        <h1 :style="{ color: textColor }">
          <span v-if="title1" :class="generateFontWeight(titleWeight)" class="title d-block">
            {{ title1 }}
          </span>
          <span
            v-if="titleExtended"
            :class="generateFontWeight(titleExtendedWeight)"
            class="title-extended d-block"
          >
            {{ titleExtended }}
          </span>
        </h1>
      </div>
      <prismic-rich-text
        v-if="textContent"
        :field="textContent"
        :style="{ color: textColor }"
        :class="true"
        class="text-container text-center"
      ></prismic-rich-text>
      <ButtonActions
        v-if="buttonText"
        :blank="buttonNewTab"
        :path="buttonUrl"
        :anchor-id-target="anchorIdTarget"
      >
        <template #action-scroll="{ item }">
          <Button
            :to="item.path"
            :blank="item.blank"
            with-gradient=""
            :gradient="buttonBackgroundColor"
            :font-color="buttonTextColor"
            :size="buttonSize"
            :extra-data="anchorIdTarget ? { event: '' } : {}"
            @click="handleClick(item.path)"
          >
            {{ buttonText }}
          </Button>
        </template>
      </ButtonActions>
    </div>
    <picture class="figure1 d-none d-xl-flex">
      <source
        v-for="{ resolution, size } in imageSizes"
        :key="resolution"
        :media="`(max-width: ${resolution}px)`"
        :srcset="`${imageRight.url}&w=${size}&fmt=webp`"
      />
      <img :src="imageRight.url" :alt="imageRight.alt" height="100%" width="auto" />
    </picture>
  </section>
</template>

<script>
import Button from 'UI/Button';
import ButtonActions from '@/components/Prismic/Landings/Utils/ButtonActions.vue';

export default {
  components: {
    Button,
    ButtonActions
  },
  props: {
    slice: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      backgroundColor: '',
      buttonBackgroundColor: '',
      buttonNewTab: '',
      buttonSize: '',
      buttonTextColor: '',
      buttonUrl: '',
      gradientOrientation: '',
      gradientStep0: '',
      gradientStep1: '',
      imageLeft: '',
      imageRight: '',
      backgroundImageUrl: '',
      mobileBackgroundImageUrl: '',
      textColor: '',
      textContent: '',
      title1: '',
      titleExtended: '',
      titleExtendedWeight: '',
      titleWeight: '',
      imageSizes: [
        { resolution: 1366, size: 460 },
        { resolution: 1536, size: 520 },
        { resolution: 1920, size: 650 }
      ],
      anchorIdTarget: ''
    };
  },
  computed: {
    generateBackgroundColor() {
      const buildGradient = this.gradientOrientation
        ? `\
        linear-gradient(\
            ${this.gradientOrientation}deg,${this.gradientStep0},${this.gradientStep1}\
        )
      `
        : '';
      return this.backgroundColor ? this.backgroundColor : buildGradient;
    },
    generateCssVariables() {
      return `
      --mobile-background-image: url(${this.mobileBackgroundImageUrl};
      --background-image: url(${this.backgroundImageUrl});
       --background-color: ${this.generateBackgroundColor};
      `;
    }
  },
  created() {
    const {
      background_color,
      button_background_color,
      button_new_tab,
      button_size,
      button_text,
      button_text_color,
      button_url,
      gradient_orientation,
      gradient_step_0,
      gradient_step_1,
      image_left,
      image_right,
      background_image,
      mobile_background_image,
      text_color,
      text_content,
      title1,
      title_extended,
      title_extended_weight,
      title_weight
    } = this.slice.primary;
    this.backgroundColor = background_color;
    this.backgroundImageUrl = background_image?.url;
    this.mobileBackgroundImageUrl = mobile_background_image?.url;
    this.buttonBackgroundColor = button_background_color || 'var(--exp-color)';
    this.buttonNewTab = button_new_tab;
    this.buttonSize = button_size;
    this.buttonText = button_text;
    this.buttonTextColor = button_text_color || '#ffffff';
    this.buttonUrl = button_url;
    this.gradientOrientation = gradient_orientation;
    this.gradientStep0 = gradient_step_0;
    this.gradientStep1 = gradient_step_1;
    this.imageLeft = image_left;
    this.imageRight = image_right;
    this.textColor = text_color;
    this.textContent = text_content;
    this.title1 = title1[0]?.text;
    this.titleExtended = title_extended[0]?.text;
    this.titleExtendedWeight = title_extended_weight;
    this.titleWeight = title_weight;
    this.anchorIdTarget = this.slice.primary.anchor_id_target;
  },
  methods: {
    generateFontWeight(value) {
      return `font-weight-${value}`;
    },
    handleClick(clickUrl) {
      this.$dataLayerPush('home_header_v3_slice_button_click', {
        homeHeaderV3: {
          clickUrl: clickUrl
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
img {
  object-fit: cover;
  width: 100%;
  height: 100%;
}
picture {
  margin: 0;
}
.header-container {
  --bg-color: var(--background-color, transparent);
  background: var(--mobile-background-image) center/cover no-repeat, var(--bg-color);
  display: grid;
  grid-auto-flow: column;
  margin-left: -23px;
  margin-right: -23px;
  @include min-xl {
    grid-template-columns: 1fr minmax(33%, auto) 1fr;
  }
  @include min-md {
    background: var(--background-image) center/cover no-repeat, var(--bg-color);
  }
  .figure0 img {
    object-position: right;
  }
  .content-container {
    --max-width-text: 45rem;
    padding: 2rem 0.5rem;
    @include min-xl {
      min-width: 550px;
      padding: 5rem 0.5rem;
    }
    gap: 0.5rem;
    .title-container {
      font-size: 46px;
      max-width: var(--max-width-text);
    }
    .text-container {
      max-width: calc(var(--max-width-text) - 11rem);
      font-size: 16px;
    }
  }
  .figure1 img {
    object-position: left;
  }
}
</style>
